import { type ReactElement, useLayoutEffect, useRef, useState } from "react"
import { Link } from "react-router"
import styled from "styled-components"
import type { StudyMaterial } from "~/features/studyEbook/types"
import { Button } from "~/shared/ui"
import { PlayIcon } from "~/shared/ui/icons/PlayIcon"

/** Figma clamps the hero description; the TV app uses the same limit. */
const DESCRIPTION_LINE_LIMIT = 4

const Root = styled.header`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing.xl};
  min-height: ${({ theme }) => theme.detail.heroMinHeight};
  padding: ${({ theme }) => `${theme.spacing.xxl} ${theme.spacing.lg} ${theme.spacing.xl}`};
  border-radius: ${({ theme }) => theme.radius.lg};
  background-color: ${({ theme }) => theme.color.pageBackground};
  overflow: hidden;
  isolation: isolate;

  ${({ theme }) => theme.mediaUp.md} {
    padding: ${({ theme }) => `${theme.spacing.xxl} ${theme.spacing.xl} ${theme.spacing.xl}`};
  }
`

const Backdrop = styled.div`
  position: absolute;
  inset: 0;
  z-index: -1;
`

const BackdropImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center top;
`

/**
 * Two scrims, as in Figma: one down the frame and one across it. Together they
 * take the artwork to `pageBackground` under the copy column, which is what lets
 * dark text sit over photography at AA contrast.
 */
const Scrim = styled.div<{ $direction: "down" | "across" }>`
  position: absolute;
  inset: 0;
  background-image: ${({ theme, $direction }) =>
    $direction === "down" ? theme.gradient.heroFadeDown : theme.gradient.heroFadeAcross};
`

const Copy = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs};
  max-width: ${({ theme }) => theme.detail.copyMaxWidth};
  color: ${({ theme }) => theme.color.textPrimary};
`

const Title = styled.h1`
  font-size: clamp(${({ theme }) => theme.fontSize.xxl}, 4vw, ${({ theme }) => theme.fontSize.display});
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  line-height: ${({ theme }) => theme.lineHeight.tight};
  overflow-wrap: anywhere;
`

const MetaLine = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  line-height: ${({ theme }) => theme.lineHeight.normal};
  /* Figma sets whitespace-pre-wrap here so the wider gap either side of the
     separator survives; HTML would otherwise collapse it to one space. */
  white-space: pre-wrap;
`

const Description = styled.p<{ $clamped: boolean }>`
  margin-top: ${({ theme }) => theme.spacing.xs};
  font-size: ${({ theme }) => theme.fontSize.lg};
  line-height: ${({ theme }) => theme.lineHeight.normal};
  color: ${({ theme }) => theme.color.textPrimary};

  ${({ $clamped }) =>
    $clamped
      ? `display: -webkit-box; -webkit-box-orient: vertical; -webkit-line-clamp: ${DESCRIPTION_LINE_LIMIT}; overflow: hidden;`
      : ""}
`

const Actions = styled.div`
  position: relative;
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.lg};
`

const StartLink = styled(Button).attrs({ as: Link })`
  text-decoration: none;
`

/** Leaves the app for Lifeway Reader, so an anchor rather than a router link. */
const MaterialLink = styled(Button).attrs({ as: "a" })`
  text-decoration: none;
`

interface DetailHeroProps {
  readonly title: string
  readonly metaLine: string
  readonly description: string | undefined
  readonly backdropUrl: string | undefined
  readonly startHref: string | undefined
  readonly hasProgress: boolean
  /** The book that accompanies this study, when one resolved. */
  readonly material: StudyMaterial | undefined
  readonly qaHook: string
}

export const DetailHero = ({
  title,
  metaLine,
  description,
  backdropUrl,
  startHref,
  hasProgress,
  material,
  qaHook,
}: DetailHeroProps): ReactElement => {
  const [isExpanded, setIsExpanded] = useState(false)
  const [isClamped, setIsClamped] = useState(false)
  const descriptionRef = useRef<HTMLParagraphElement>(null)

  /**
   * Figma always draws "More info" beside "Start", but a button that expands
   * nothing is worse than no button — so it appears only once the clamp is
   * actually hiding text, matching the TV screen's `onTruncatedChange`. The
   * measurement is skipped while expanded, where scroll and client height are
   * equal by definition and would retract the button mid-interaction.
   */
  useLayoutEffect(() => {
    const node = descriptionRef.current

    if (!node || isExpanded) {
      return
    }

    const measure = () => setIsClamped(node.scrollHeight > node.clientHeight + 1)

    measure()

    const observer = new ResizeObserver(measure)
    observer.observe(node)

    return () => observer.disconnect()
  }, [isExpanded])

  return (
    <Root data-qa-hook={qaHook}>
      <Backdrop>
        {backdropUrl ? (
          <BackdropImage src={backdropUrl} alt="" decoding="async" data-qa-hook={`${qaHook}-image`} />
        ) : null}
        <Scrim $direction="across" />
        <Scrim $direction="down" />
      </Backdrop>

      <Copy>
        <Title data-qa-hook={`${qaHook}-title`}>{title}</Title>
        {metaLine ? <MetaLine data-qa-hook={`${qaHook}-meta`}>{metaLine}</MetaLine> : null}
        {description ? (
          <Description
            ref={descriptionRef}
            $clamped={!isExpanded}
            id={`${qaHook}-description`}
            data-qa-hook={`${qaHook}-description`}
          >
            {description}
          </Description>
        ) : null}
      </Copy>

      <Actions>
        {startHref ? (
          <StartLink to={startHref} data-qa-hook={`${qaHook}-start`}>
            {hasProgress ? "Resume" : "Start"}
            <PlayIcon />
          </StartLink>
        ) : null}
        {material ? (
          <MaterialLink
            $variant="outline"
            href={material.href}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`${material.title} (opens in Lifeway Reader)`}
            data-qa-hook={`${qaHook}-material`}
            data-reader-id-kind={material.linkIdKind}
          >
            Read the Book
          </MaterialLink>
        ) : null}
        {description && (isClamped || isExpanded) ? (
          <Button
            type="button"
            $variant="outline"
            aria-expanded={isExpanded}
            aria-controls={`${qaHook}-description`}
            data-qa-hook={`${qaHook}-more-info`}
            onClick={() => setIsExpanded(expanded => !expanded)}
          >
            {isExpanded ? "Less info" : "More info"}
          </Button>
        ) : null}
      </Actions>
    </Root>
  )
}
